import { useMutation, useQuery } from 'react-query';
import * as rawProductService from 'services/rawProductService';
import * as productSuggestionService from 'services/productSuggestionService';

// The review queue: every raw product that has not become a product, with what
// each one is waiting for.
export const useGetRawProductsForReview = (
  dto: rawProductService.ListRawProductsForReviewDto,
  options = {},
) => {
  return useQuery({
    queryKey: ['rawProductsForReview', dto],
    queryFn: () => rawProductService.getRawProductsForReview(dto),
    keepPreviousData: true,
    ...options,
  });
};

// Turns a finished raw product into a real one.
export const usePromoteRawProduct = (
  onSuccess?: (result: rawProductService.PromoteRawProductResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.promoteRawProduct, {
    onSuccess,
    onError,
    ...options,
  });
};

// One page of the raw products sitting under a category, for the browse tree.
export const useGetRawProductsByCategory = (
  dto: rawProductService.ListRawProductsByCategoryDto,
  options = {},
) => {
  return useQuery({
    queryKey: ['rawProductsByCategory', dto],
    queryFn: () => rawProductService.getRawProductsByCategory(dto),
    ...options,
  });
};

// Promoted and filed counts for every category, for the browse tree's headings.
export const useGetRawProductCategoryCounts = (options = {}) => {
  return useQuery({
    queryKey: ['rawProductCategoryCounts'],
    queryFn: () => rawProductService.getRawProductCategoryCounts(),
    ...options,
  });
};

// Moves one raw product to another leaf while browsing the tree.
export const useMoveRawProductToLeafCategory = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.moveRawProductToLeafCategory, {
    onSuccess,
    onError,
    ...options,
  });
};

// The whole taxonomy, fetched once and reused by every card's picker.
export const useGetLeafCategories = (options = {}) => {
  return useQuery({
    queryKey: ['leafCategories'],
    queryFn: () => rawProductService.getLeafCategories(),
    staleTime: Infinity,
    ...options,
  });
};

// Clears the product's personal-care verdict so the scope batch screens it
// again, and takes it out of the categorization queue.
export const useResetRawProductType = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.resetRawProductType, {
    onSuccess,
    onError,
    ...options,
  });
};

export const useReextractRawProductOcr = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.reextractRawProductOcr, {
    onSuccess,
    onError,
    ...options,
  });
};

export const useReextractRawProductForm = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.reextractRawProductForm, {
    onSuccess,
    onError,
    ...options,
  });
};

// Writes one product's description again. Its result is the new sentences, so
// the page updates that one card from them rather than refetching the queue -
// the description touches no other field and no verdict, and a refetch would
// reorder or drop the row the reviewer is in the middle of reading.
export const useRedescribeRawProduct = (
  onSuccess?: (result: rawProductService.RedescribeRawProductResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.redescribeRawProduct, {
    onSuccess,
    onError,
    ...options,
  });
};

// A reviewer's rewrite of the description. Its answer is the same shape as a
// re-read's, so the page updates that one card from it rather than refetching
// the queue.
export const useUpdateRawProductDescription = (
  onSuccess?: (result: rawProductService.RedescribeRawProductResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductDescription, {
    onSuccess,
    onError,
    ...options,
  });
};

// Reads what the pack holds again for one product. Like the description, its
// answer is what the row now holds, so the page updates that one card from it -
// contents touch no other field and no verdict, and a refetch would reorder the
// queue under a reviewer mid-read.
export const useReextractRawProductContents = (
  onSuccess?: (result: rawProductService.ReextractRawProductContentsResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.reextractRawProductContents, {
    onSuccess,
    onError,
    ...options,
  });
};

// Writes how one product is used again. Its answer is what the row now holds,
// so the page updates that card from it rather than refetching the queue.
export const useReextractRawProductUsages = (
  onSuccess?: (result: rawProductService.ReextractRawProductUsagesResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.reextractRawProductUsages, {
    onSuccess,
    onError,
    ...options,
  });
};

// Reads what one product's pack asserts about itself again. Its answer is what
// the row now holds, so the page updates that card from it rather than
// refetching the queue.
export const useReextractRawProductAttributes = (
  onSuccess?: (result: rawProductService.ReextractRawProductAttributesResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(
    ({ rawProductId, withDescription }: { rawProductId: string; withDescription?: boolean }) =>
      rawProductService.reextractRawProductAttributes(rawProductId, withDescription),
    {
      onSuccess,
      onError,
      ...options,
    },
  );
};

// A reviewer's correction to those attributes, answering in the same shape.
export const useUpdateRawProductAttributes = (
  onSuccess?: (result: rawProductService.ReextractRawProductAttributesResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductAttributes, {
    onSuccess,
    onError,
    ...options,
  });
};

// A reviewer's correction to the cautions. Its answer is what the row now holds,
// in the same shape a re-read gives, so the page updates that one card from it
// rather than refetching the queue.
export const useUpdateRawProductWarnings = (
  onSuccess?: (result: rawProductService.ReextractRawProductWarningsResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductWarnings, {
    onSuccess,
    onError,
    ...options,
  });
};

// A reviewer's correction to what the pack holds. Its answer is what the row now
// holds, in the same shape a re-read gives, so the page updates that one card
// from it rather than refetching the queue.
export const useUpdateRawProductContents = (
  onSuccess?: (result: rawProductService.ReextractRawProductContentsResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductContents, {
    onSuccess,
    onError,
    ...options,
  });
};

// A reviewer's correction to the procedures. Its answer is what the row now
// holds, in the same shape a re-read gives, so the page updates that one card
// from it rather than refetching the queue.
export const useUpdateRawProductUsages = (
  onSuccess?: (result: rawProductService.ReextractRawProductUsagesResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductUsages, {
    onSuccess,
    onError,
    ...options,
  });
};

// Reads one product's ingredients panel without storing it, for the reviewer to
// correct and confirm in the modal first.
export const usePreviewRawProductIngredients = (
  onSuccess?: (result: rawProductService.PreviewRawProductIngredientsResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.previewRawProductIngredients, {
    onSuccess,
    onError,
    ...options,
  });
};

// Stores a reviewer's confirmed ingredients list. Its answer is what the row now
// holds, so the page updates that card from it rather than refetching.
export const useUpdateRawProductIngredients = (
  onSuccess?: (result: rawProductService.ReextractRawProductIngredientsResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductIngredients, {
    onSuccess,
    onError,
    ...options,
  });
};

// Reads one product's printed warnings again, from the photos the reviewer
// ticked. Like the other label-reading passes, its answer replaces what the card
// is showing rather than refetching the queue.
export const useReextractRawProductWarnings = (
  onSuccess?: (result: rawProductService.ReextractRawProductWarningsResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.reextractRawProductWarnings, {
    onSuccess,
    onError,
    ...options,
  });
};

// Orders one product's stored panel and names every row in Arabic. Its answer is
// what the row now holds, so the page updates that card from it rather than
// refetching the queue.
export const useRankRawProductIngredients = (
  onSuccess?: (result: rawProductService.RankRawProductIngredientsResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.rankRawProductIngredients, {
    onSuccess,
    onError,
    ...options,
  });
};

// Soft-deletes the suggestion, which takes its raw product down with it. For
// products whose photos turn out to show more than one thing, so the whole
// entry is bad rather than just its category.
export const useDeleteProductSuggestion = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(productSuggestionService.deleteProductSuggestion, {
    onSuccess,
    onError,
    ...options,
  });
};

// Removes one photo from a suggestion whose other images are fine.
export const useDeleteSuggestionImage = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(productSuggestionService.deleteSuggestionImage, {
    onSuccess,
    onError,
    ...options,
  });
};

// One identity pass run again, for a single field on the card.
export const useRerunRawProductStep = (
  onSuccess?: (result: rawProductService.RerunRawProductStepResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.rerunRawProductStep, {
    onSuccess,
    onError,
    ...options,
  });
};

// A reviewer naming the front photo from the card's own strip.
export const useSetRawProductAvatar = (
  onSuccess?: (result: rawProductService.SetRawProductAvatarResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.setRawProductAvatar, {
    onSuccess,
    onError,
    ...options,
  });
};

// A reviewer's correction to the identity fields. Its result is what the row now
// holds, so the page updates that one card from it rather than refetching the
// queue - a refetch would reorder or drop the card the reviewer is working in.
export const useUpdateRawProductIdentity = (
  onSuccess?: (result: rawProductService.UpdateRawProductIdentityResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductIdentity, {
    onSuccess,
    onError,
    ...options,
  });
};

// A reviewer's correction to the pack's particulars. Its result is what the row
// now holds, so the page updates that card from it rather than refetching.
export const useUpdateRawProductInfo = (
  onSuccess?: (result: rawProductService.UpdateRawProductInfoResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductInfo, {
    onSuccess,
    onError,
    ...options,
  });
};

// Reads one product's particulars again. Its result is what the row now holds,
// so the page updates that card from it rather than refetching the queue.
export const useReextractRawProductInfo = (
  onSuccess?: (result: rawProductService.UpdateRawProductInfoResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.reextractRawProductInfo, {
    onSuccess,
    onError,
    ...options,
  });
};

// Every brand, for the brand picker. Refetched after a save, since a save can
// rename a brand or add one.
export const useGetBrands = (options = {}) => {
  return useQuery({
    queryKey: ['brands'],
    queryFn: () => rawProductService.getBrands(),
    ...options,
  });
};

// A reviewer's correction to the brand a pack is filed under.
export const useUpdateRawProductBrand = (
  onSuccess?: (result: rawProductService.ReextractRawProductBrandResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.updateRawProductBrand, {
    onSuccess,
    onError,
    ...options,
  });
};

// Reads one product's brand again. Its result is the brand the row now points
// at, so the page updates that card from it rather than refetching the queue.
export const useReextractRawProductBrand = (
  onSuccess?: (result: rawProductService.ReextractRawProductBrandResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.reextractRawProductBrand, {
    onSuccess,
    onError,
    ...options,
  });
};

// Runs kind, area, audience, form, pack shape and category again for one
// product. Its result is what the row now holds.
export const useRunMissingRawProductPasses = (
  onSuccess?: (result: rawProductService.RunMissingRawProductPassesResult) => void,
  onError?: (error: unknown, variables: { rawProductId: string; withContents: boolean }) => void,
  options = {},
) => {
  return useMutation(rawProductService.runMissingRawProductPasses, {
    onSuccess,
    onError,
    ...options,
  });
};

export const useRerunRawProductPipeline = (
  onSuccess?: (result: rawProductService.RerunRawProductPipelineResult) => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.rerunRawProductPipeline, {
    onSuccess,
    onError,
    ...options,
  });
};

export const useResolveRawProductCategory = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void,
  options = {},
) => {
  return useMutation(rawProductService.resolveRawProductCategory, {
    onSuccess,
    onError,
    ...options,
  });
};
